import { useState } from 'react';
import CustomInput from '../../../components/atoms/CustomInput';
import GradientButton from '../../../components/atoms/GradientButton';
import GradientOutlineButton from '../../../components/atoms/GradientOutlineButton';
import FlatpickrInput from '../../../components/atoms/DataTimePicker';
import { formatDate, formatTime, openPdf } from '../../../utils/hooks/utils';
import generalHistory from '../../../assets/documents/new_patient_intake.pdf';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Schedule = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    message: '',
    preferred_date: '',
    preferred_time: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const encode = (data) => {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key])
      )
      .join('&');
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    setLoading(true);

    await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({
        'form-name': 'contact-us-schedule-form',
        ...formData,
        preferred_date: formatDate(formData.preferred_date),
        preferred_time: formatTime(formData.preferred_time),
      }),
    })
      .then(() => {
        toast.success(
          'Thank you for submitted the form! A staff member will be reaching out to you soon!',
          {
            progressClassName: 'gradient-progress-bar',
          }
        );
        setFormData({
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          message: '',
          preferred_date: '',
          preferred_time: '',
        });
        setLoading(false);
      })
      .catch((error) => {
        toast.error(`Failed to send message: ${error.message}`);
        setLoading(false);
      });
  };

  const handleFlatpickrChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="w-full flex flex-col lg:flex-row gap-4 items-center sm:gap-10 md:gap-24 max-w-[1238px] mx-auto px-4 md:px-10 py-10 md:py-20 relative z-10">
      <div className="flex-[55%] flex flex-col gap-2">
        <h2 className="text-[38px] sm:text-[40px] md:text-[54px] text-primary font-bold">
          Schedule an Evaluation
        </h2>
        <p className="text-[#606060] font-normal text-lg 2xl:text-2xl">
          Please call or submit the form below to schedule an evaluation or to
          contact us for any reason. Please let us know what date and time works
          best for you. We will do our best to accommodate your schedule as we
          have no waitlists. Please note, appointments are not scheduled until
          Guidepost staff replies to this form confirming appointment.
        </p>
        <div className="text-[#606060] font-secondary ">
          <p className="mt-3 text-lg leading-tight lg:text-3xl 3xl:text-4xl">
            CALL US
          </p>
          <p className=" text-[24px] lg:text-4xl 3xl:text-5xl cursor-pointer font-custom font-bold text-[#D92D8A]">
            <a href="tel:+19073764880"> </a>
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <p className="text-[#606060] font-normal text-lg 2xl:text-2xl">
            At your earliest convenience, please download the
            <span className="text-[#760093]">
              &nbsp;Guidepost Pediatric Intake General History Form{' '}
              <button
                onClick={() => openPdf(generalHistory)}
                className="hover:underline"
              >
                HERE.
              </button>
              &nbsp;
            </span>
            You will need to complete and submit all intake forms before your
            evaluation.
          </p>
          <GradientOutlineButton
            text="Intake Forms"
            isDark={false}
            navigateTo={'/resources/forms'}
          />
        </div>
      </div>
      <div className="flex-[45%] w-full">
        <form className="flex flex-col w-full gap-3" onSubmit={sendEmail}>
          <p className="hidden">
            <label>
              Don't fill this out if you're human:
              <input name="bot-field" type="text" />
            </label>
          </p>
          <h2 className="text-[28px] sm:text-[40px] text-primary font-semibold">
            Enter your information
          </h2>
          <div className="flex flex-col w-full gap-4 sm:flex-row">
            <CustomInput
              placeholder="first name"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
            <CustomInput
              placeholder="last name"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </div>
          <CustomInput
            placeholder="phone"
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
          />
          <CustomInput
            placeholder="email"
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <textarea
            placeholder="Message"
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="
              bg-primary text-white placeholder-white/60 
              h-28 px-2 xl:px-3 rounded-md focus:outline-none 
              focus:ring-2 focus:ring-[#5F007A]
              w-full
            "
          />
          <div className="flex flex-col w-full gap-4 sm:flex-row">
            <FlatpickrInput
              type="date"
              placeholder="Preferred Date"
              name="preferred_date"
              value={formData.preferred_date}
              onChange={(date) => handleFlatpickrChange('preferred_date', date)}
              required
            />

            <FlatpickrInput
              type="time"
              placeholder="Preferred Time"
              name="preferred_time"
              value={formData.preferred_time}
              onChange={(time) => handleFlatpickrChange('preferred_time', time)}
              required
            />
          </div>
          <div className="flex flex-col justify-between gap-4 sm:flex-row">
            <GradientButton
              text={loading ? 'Sending...' : 'Submit'}
              width={'w-full sm:w-[150px]'}
              padding="py-2 md:py-0"
              height="h-[38px]"
              disabled={loading}
            />
          </div>
        </form>
      </div>
      <ToastContainer position="bottom-right" autoClose={3000} theme="dark" />
    </div>
  );
};

export default Schedule;
